import { LegalProcessesApi } from 'dashboard/api/legalProcesses';

const state = {
  records: [],
  selectedProcess: null,
  stats: null,
  // Ações já usadas na conta ("Reclamação trabalhista", "Carta precatória").
  // Vêm no meta da listagem e alimentam as sugestões do campo Ação.
  actionNames: [],
  uiFlags: {
    isFetching: false,
    // A FALHA PRECISA TER NOME PRÓPRIO.
    //
    // Sem esta bandeira, `fetch` terminava em `finally` e a tela caía no
    // estado VAZIO — ou seja, a falha de rede se disfarçava de "nenhum
    // processo". Para um advogado essas duas frases são coisas
    // completamente diferentes: uma é informação, a outra é motivo para achar
    // que perdeu dado.
    hasError: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getProcesses: $state => $state.records,
  getSelectedProcess: $state => $state.selectedProcess,
  getStats: $state => $state.stats,
  getActionNames: $state => $state.actionNames,
  getUIFlags: $state => $state.uiFlags,
};

const mutations = {
  SET_PROCESSES($state, processes) {
    $state.records = processes;
  },
  SET_SELECTED_PROCESS($state, process) {
    $state.selectedProcess = process;
  },
  SET_STATS($state, stats) {
    $state.stats = stats;
  },
  SET_ACTION_NAMES($state, names) {
    $state.actionNames = names || [];
  },
  ADD_PROCESS($state, process) {
    $state.records.unshift(process);
  },
  UPDATE_PROCESS($state, updated) {
    const index = $state.records.findIndex(p => p.id === updated.id);
    if (index !== -1) {
      $state.records.splice(index, 1, updated);
    }
    if ($state.selectedProcess?.id === updated.id) {
      $state.selectedProcess = updated;
    }
  },
  REMOVE_PROCESS($state, id) {
    $state.records = $state.records.filter(p => p.id !== id);
    if ($state.selectedProcess?.id === id) {
      $state.selectedProcess = null;
    }
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

// O id do último processo pedido por `fetchById` — ver o comentário lá.
let ultimoPedidoDeProcesso = null;

const actions = {
  async fetch({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetching: true, hasError: false });
    try {
      const { data } = await LegalProcessesApi.getAll(params);
      commit('SET_PROCESSES', data.payload);
      commit('SET_ACTION_NAMES', data.meta?.action_names);
    } catch (error) {
      // Era `try/finally` sem `catch`: a rejeição subia para quem chamou, e
      // quem chama (`loadProcesses`) despachava sem `.catch()`. Resultado: a
      // falha virava rejeição não tratada, engolida pelo navegador, e a tela
      // mostrava o estado vazio.
      commit('SET_UI_FLAG', { hasError: true });
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  // Só a resposta do ÚLTIMO pedido vira o processo aberto. Trocando de
  // processo depressa (busca global, anterior/próximo), a resposta do
  // anterior pode chegar DEPOIS da do atual e pôr o cabeçalho de outro
  // cliente na ficha (relato do Dr. Henrique, 01/10/2026).
  async fetchById({ commit }, id) {
    ultimoPedidoDeProcesso = String(id);
    const { data } = await LegalProcessesApi.getById(id);
    if (String(id) === ultimoPedidoDeProcesso) {
      commit('SET_SELECTED_PROCESS', data.payload);
    }
    return data.payload;
  },

  async create({ commit }, processData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await LegalProcessesApi.create(processData);
      commit('ADD_PROCESS', data.payload);
      return data.payload;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, ...processData }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await LegalProcessesApi.update(id, processData);
      commit('UPDATE_PROCESS', data.payload);
      return data.payload;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async destroy({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await LegalProcessesApi.delete(id);
      commit('REMOVE_PROCESS', id);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async fetchStats({ commit }) {
    // Sem `catch`, a rejeição subia e quem chama despachava sem `.catch()` —
    // virava rejeição não tratada. Os números somem (o painel é `v-if="stats"`),
    // o que é honesto: melhor nenhum número do que número velho. O que não
    // pode é a falha sumir também.
    try {
      const { data } = await LegalProcessesApi.getStats();
      commit('SET_STATS', data.payload);
      return data.payload;
    } catch (error) {
      commit('SET_STATS', null);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
